import React, { useEffect, useState } from 'react';
import * as THREE from 'three';
import { EventDispatcher, Scene } from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useStableFunction } from '../../core/hooks/use-stable-function';
import { Icon } from '../../visual-components/icon/icon';
import { Typography } from '../../visual-components/typography/typography';
import isNil, { notNil } from '../../core/helpers/is-nil';
import { IMGIX_DYNAMIC_BASE_URL } from '../../core/config';
import { ThreeDModelEvent } from '../../pages/article/overview';
import { useTrackOnce } from '../analytics/use-track-once';
import { TransformControls } from './transform-controls';
import { useEscapeKeyListener } from './use-escape-key-listener';
import { useKnowsHowToUse3dViewer } from './use-knows-how-to-use-3d-viewer';
import styles from './three-d-model.module.scss';

type Props = {
  detail: {
    eventHub: EventDispatcher<ThreeDModelEvent>;
    isFocused: boolean;
    setFocused: (focused: boolean) => void;
  } | null;
  modelId: string;
};

const CHANGED_OBJECT_THRESHOLD = 5;

export const ThreeDModel: React.FC<Props> = ({ detail, modelId }) => {
  const { t } = useTranslation();
  const listener = useStableFunction(() => {
    detail?.setFocused(true);
  });
  const track3dInteracted = useTrackOnce({ type: '3d-interacted' });
  const { setKnowsHowToUse3DViewer } = useKnowsHowToUse3dViewer();

  const [modelLoaded, setModelLoaded] = useState(false);
  const [resizeTrigger, setResizeTrigger] = useState(0);

  const isOfflinePreview = isNil(detail);
  const eventHub = detail?.eventHub;
  useEffect(() => {
    // Prepare model
    const loader = new GLTFLoader();

    let currentScene: null | Scene = null;
    let currentRendererCallback: null | (() => void) = null;
    let currentGltf: null | { unmount: () => void; mount: (scene: Scene, cb: () => void) => void } = null;

    // Mouse Interaction Tracker
    let changedObjectCounter = 0;

    let initiateObjectReset = false;
    let rotationObject: THREE.Object3D | null = null;

    const closeListener = () => {
      initiateObjectReset = true;
    };
    eventHub?.addEventListener('close', closeListener);

    const clickListener = () => {
      if (changedObjectCounter > CHANGED_OBJECT_THRESHOLD) {
        changedObjectCounter = 0;
        return;
      }
      listener();
    };

    function loadModelAndUnmountExisting(uncheckedName = modelId) {
      currentGltf?.unmount();
      loader.load(`${IMGIX_DYNAMIC_BASE_URL}/3d/models/${uncheckedName}.glb`, (gltf) => {
        const rawModel = gltf.scene;
        const newModel = controlStrategy.initModelPre(rawModel);
        const model = newModel || rawModel;

        const boundingBox = new THREE.Box3().setFromObject(model);

        const center = new THREE.Vector3();
        boundingBox.getCenter(center);

        model.position.sub(center);

        currentGltf = {
          unmount: () => {
            scene?.remove();
          },
          mount: (scene: Scene, currentRendererCallback: () => void) => {
            scene.add(model);
            currentRendererCallback();
            setModelLoaded(true);
          },
        };

        if (currentScene && currentRendererCallback) {
          currentGltf.mount(currentScene, currentRendererCallback);
        }
      });
    }

    loadModelAndUnmountExisting();

    const container = document.getElementById('sample-container');
    if (!container) {
      return;
    }

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
    });

    // NOTE: this performs rather poorly, but for zoom it's important to keep high-res
    // Use 1x device pixel ratio for offline rendering to have consistent results – prefer to upscale the canvas
    renderer.setPixelRatio(isOfflinePreview ? 1 : window.devicePixelRatio * 2);

    const scene = new THREE.Scene();

    const camera = new THREE.PerspectiveCamera(7.161, container.clientWidth / container.clientHeight, 0.1, 1000);

    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    pmremGenerator.compileEquirectangularShader();

    const neutralEnvironment = pmremGenerator.fromScene(new RoomEnvironment()).texture;
    scene.environment = neutralEnvironment;

    const rotationAngle = Math.PI / 5;
    scene.rotation.y = -rotationAngle;

    renderer.toneMapping = THREE.NeutralToneMapping;
    const exposure = -0.25;
    renderer.toneMappingExposure = Math.pow(2, exposure);

    const transformControlStrategy = {
      buildControls: () => {
        const controls = new TransformControls(camera, renderer.domElement);
        controls.setMode('rotate');

        controls.addEventListener('objectChange', () => {
          changedObjectCounter += 1;
          initiateObjectReset = false;
          setKnowsHowToUse3DViewer(true);
          track3dInteracted();
        });
        container.addEventListener('click', clickListener);

        return controls;
      },
      initModelPre: (model: THREE.Object3D) => {
        scene.add(controls.getHelper());
        camera.lookAt(new THREE.Vector3(0, 0, 0));

        const wrapper = new THREE.Object3D();

        const boundingBox = new THREE.Box3().setFromObject(model);
        const center = new THREE.Vector3();
        boundingBox.getCenter(center);

        model.position.sub(center);

        wrapper.add(model);

        controls.attach(wrapper);
        rotationObject = wrapper;

        return wrapper;
      },
    };

    const controlStrategy = transformControlStrategy;

    const controls = controlStrategy.buildControls();

    // controls.update() must be called after any manual changes to the camera's transform
    const originalCameraPosition = new THREE.Vector3(0, 0, isOfflinePreview ? 122.14 : 136);
    const rotatedCameraPosition = originalCameraPosition
      .clone()
      .applyAxisAngle(new THREE.Vector3(0, 1, 0), -rotationAngle);

    camera.position.copy(rotatedCameraPosition);
    controls.update(0);

    renderer.setSize(container.clientWidth, container.clientHeight);
    container.appendChild(renderer.domElement);

    function animate() {
      if (initiateObjectReset && rotationObject) {
        const currentRotationY = rotationObject.rotation.y % (2 * Math.PI);
        const delta = 0 - currentRotationY;

        let neededAdjustment = false;
        if (Math.abs(delta) < 0.01) {
          rotationObject.rotation.y = 0;
        } else {
          rotationObject.rotation.y += delta * 0.1;
          neededAdjustment = true;
        }

        const currentRotationX = rotationObject.rotation.x % (2 * Math.PI);
        const deltaX = 0 - currentRotationX;
        if (Math.abs(deltaX) < 0.01) {
          rotationObject.rotation.x = 0;
        } else {
          rotationObject.rotation.x += deltaX * 0.1;
          neededAdjustment = true;
        }

        if (!neededAdjustment) {
          initiateObjectReset = false;
        }
      }
      renderer.render(scene, camera);
    }
    renderer.setAnimationLoop(animate);

    currentScene = scene;
    let isDisposed = false;
    currentRendererCallback = () => {
      if (isDisposed) {
        return;
      }
      if (isOfflinePreview && window.igpOfflineRenderer) {
        renderer.render(scene, camera);
        // FIXME: @Simon some env map / … still is loaded async
        window.igpOfflineRenderer?.(renderer.domElement.toDataURL());
      }
    };
    if (currentGltf) {
      // TODO: @Simon when extracting model loading, it might actually already be loaded
      //currentGltf.mount(scene, currentRendererCallback);
    }

    return () => {
      isDisposed = true;
      renderer.dispose();
      container.removeChild(renderer.domElement);
      container.removeEventListener('click', clickListener);
      eventHub?.removeEventListener('close', closeListener);
    };
  }, [
    listener,
    isOfflinePreview,
    modelId,
    resizeTrigger,
    setKnowsHowToUse3DViewer,
    eventHub,
    setModelLoaded,
    track3dInteracted,
  ]);

  useEffect(() => {
    if (isOfflinePreview) {
      return;
    }
    function handleResize() {
      setResizeTrigger((old) => old + 1);
    }
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isOfflinePreview, setResizeTrigger]);

  useEscapeKeyListener(
    () => {
      detail?.setFocused(false);
    },
    notNil(detail) && detail?.isFocused,
  );

  return (
    <>
      <div
        className={classNames(styles.canvasContainer, {
          [styles.large]: isOfflinePreview,
          [styles.fadeIn]: modelLoaded,
          [styles.moveOut]: detail?.isFocused ?? false,
        })}
      >
        <div className={styles.overflowContainer}>
          <div
            id="sample-container"
            className={classNames(styles.transformContainer, {
              [styles.moveOut]: detail?.isFocused ?? false,
            })}
          />
        </div>
        {notNil(detail) ? (
          <div
            className={classNames(styles.interactionIndicator, { [styles.hidden]: !modelLoaded || detail.isFocused })}
          >
            <Typography className={styles.cta} variant="buttontext">
              {t('articleDetail.3d.interactionHint')}
            </Typography>
            <Icon size="48" sizeS="32" variant="3d-view" />
          </div>
        ) : null}
      </div>
    </>
  );
};
